import dayjs from 'dayjs';
import { useStoreState } from 'easy-peasy';
import { PropTypes } from 'prop-types';
import React, { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { ConditionalRender } from '@ge/components/conditional-render';
import { FileThumbnail } from '@ge/components/file-thumbnail';
import { Icon, Icons } from '@ge/components/icon';
import { Loader } from '@ge/components/loader';
import { ScrollingContainer } from '@ge/components/scrolling-container';
import { ToggleButton } from '@ge/components/toggle-button';
import { placements, Tooltip } from '@ge/components/tooltip';
import { DateTimeFormats } from '@ge/models/constants';
import {
  NotesCategory,
  SpecialInstructionStatus,
  NotesScope,
  Capability,
  PermissionScope,
} from '@ge/models/constants';
import ActionsMenu from '@ge/shared/components/actions-menu';
import { AuthRender } from '@ge/shared/components/auth-render';
import { useCaseLinkedTasks } from '@ge/shared/components/use-linked-task-menu';
import { useAuth } from '@ge/shared/data-hooks';
import { useActionsMenu } from '@ge/shared/data-hooks';
import { ActionKeys, NotesMenuItems } from '@ge/shared/models/actions-menu-items';
import { getNotesDisabledActions, getNotesDisabledEyeActions } from '@ge/shared/util/general';
import { isBeforeToday } from '@ge/shared/util/time-date';
import { typography } from '@ge/tokens';
import { elevations } from '@ge/tokens/elevations';

import viewing_off from '../viewing_off.svg';
import viewing_on from '../viewing_on.svg';
const StyledNotesAttachmentsTable = styled.div`
  .header {
    display: flex;
    align-items: flex-end;
    margin-left: 175px;
    justify-content: flex-end;
  }
  .note-attachment {
    &:last-of-type {
      margin-bottom: 50px;
      .row > div {
        &:nth-of-type(2),
        &:last-of-type {
          border-bottom: 1px solid ${(props) => props.theme.entityDetails.notes.separator};
        }
      }
    }
    .row {
      > div {
        padding: 25px 0;
        &:nth-of-type(2),
        &:last-of-type {
          border-top: 1px solid ${(props) => props.theme.entityDetails.notes.separator};
        }
      }
    }
  }
  .row {
    display: flex;
    align-items: stretch;
    margin: 0 0 0 175px;
    &:first-of-type {
      margin: 0px;
    }
    &:nth-of-type(2) {
      div {
        margin-top: 0;
      }
    }
  }
  .actions-header {
    color: ${(props) => props.theme.entityDetails.notes.tableCopyHeader};
    font-weight: ${typography.weight.medium};
    padding: 8px 0;
  }
  .actions-header,
  .actions {
    width: 75px;
    text-align: center;
  }
  .metadata-container {
    width: 150px;
  }
  .date-created,
  .date-updated,
  .contributed-by {
    span {
      display: block;
      &:first-of-type {
        color: ${(props) => props.theme.entityDetails.notes.tableCopyHeader};
        font-weight: ${typography.weight.medium};
        line-height: 13px;
        padding-top: 0;
      }
      &:last-of-type {
        font-weight: ${typography.weight.medium};
        font-size: 13px;
        line-height: 18px;
        margin-bottom: 15px;
      }
    }
  }
  .date-updated span:last-of-type {
    margin-bottom: 0 !important;
  }
  .notes-body {
    overflow: hidden;
    width: 505px;
    span {
      display: block;
      &.notes-title {
        color: ${(props) => props.theme.entityDetails.notes.tableCopyHeader};
        line-height: 15px;
        margin-bottom: 8px;
        svg {
          margin-right: 5px;
        }
      }
      &.notes-content {
        font-weight: ${typography.weight.regular};
        color: ${(props) => props.theme.entityDetails.notes.entityDescription};
        font-size: 16px;
        line-height: 19px;
        letter-spacing: -0.33px;
      }
      &.note-scrolling-container-wrapper {
        margin-top: 15px;
        display: flex;
        height: 125px;
        margin-top: 10px;
        .notes-attachments-container {
          display: flex;
          height: 125px;
          .note-attachment {
            margin-right: 10px;
            .attachment-thumbnail img {
              width: 125px;
              height: 93px;
            }
            .attachment-name {
              color: ${(props) => props.theme.entityDetails.notes.tableCopyHeader};
              font-weight: ${typography.weight.medium};
              line-height: 13px;
            }
          }
        }
      }
    }
    .status {
      float: right;
    }
    a {
      color: inherit;
      text-decoration: underline;
    }
    a:hover {
      color: #1e6978;
    }
  }
  .actions {
    text-align: center;
    width: 45px;
    div {
      display: contents;
    }
    img {
      vertical-align: middle;
    }
  }
  .cursorPointer {
    cursor: pointer;
  }
  .cursorNotAllowed {
    cursor: not-allowed;
  }
  .description {
    font-size: 13px;
    line-height: 18px;
  }
  .valid-until {
    color: ${(props) => props.theme.entityDetails.notes.entityDescription};
    font-weight: ${typography.weight.bold};
    line-height: 15px;
    font-size: 12px;
    text-transform: uppercase;
    &.past-due {
      color: ${(props) => props.theme.entityDetails.notes.invalidSpecialInstruction};
      font-weight: ${typography.weight.bolder};
      line-height: 14px;
      letter-spacing: 0.55px;
    }
  }
`;

const IconContainer = styled.div`
  align-items: center;
  background: ${(props) =>
    props.invalid
      ? props.theme.entityDetails.notes.invalidSpecialInstruction
      : props.theme.entityDetails.notes.validSpecialInstruction};
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: inline-block;
  margin-right: 5px;
`;

const SpecialInstructionIcon = styled(Icon).attrs((props) => ({
  color: props.theme.entityDetails.notes.entityDescription,
  size: 12,
  icon: props.icon,
  viewbox: '-2.5 -1 12 12',
}))``;

const NotesIcon = styled(Icon).attrs((props) => ({
  color: props.theme.entityDetails.notes.tableCopyHeader,
  size: 12,
  icon: props.icon,
}))``;

const LoaderContainer = styled.div`
  min-height: 200px;
`;

export const CaseNotesAttachmentsTable = ({
  notesAttachments,
  editNoteAction,
  editNoteAndSI,
  toggleHandler,
  deleteNoteAction,
  isLoading,
  entity: caseDetails,
}) => {
  const { t } = useTranslation(['entity-details']);
  const containerRef = useRef(null);

  const getEntityPanelCase = useStoreState((store) => store.cases.getEntityPanelCase);
  const getTaskById = useStoreState((store) => store.tasks.getTaskById);
  // data hooks
  const getAuthorizedMenuItems = useActionsMenu({ menuItems: NotesMenuItems });

  const { isAuthorized } = useAuth();
  const isNotesAdmin = isAuthorized({
    capabilities: [{ capability: Capability.NOTES_ADMIN, scopes: [PermissionScope.ADMIN] }],
  });

  const { isLoading: isTasksLoading } = useCaseLinkedTasks({ caseId: caseDetails?.id });

  const actionMenuHandler = (action, note) => {
    if (action === ActionKeys.NOTE_EDIT) {
      editNoteAction(note);
    } else {
      deleteNoteAction(note);
    }
  };

  const actionToggleHandler = (action, note) => {
    if (getNotesDisabledEyeActions(note, isNotesAdmin)) {
      toggleHandler(action, note);
    }
  };

  const toggleButtonHandler = (note) => {
    if (note.status === SpecialInstructionStatus.ACTIVE) {
      note.status = SpecialInstructionStatus.INACTIVE;
    } else {
      note.status = SpecialInstructionStatus.ACTIVE;
    }
    editNoteAndSI(note);
  };

  const getTitle = useCallback(
    (note) => {
      if (caseDetails.id === note.domainId) {
        return (
          <>
            <NotesIcon icon={caseDetails.childCaseIds?.length ? Icons.CASE : Icons.ANOMALY} />
            {caseDetails.description}
          </>
        );
      }
      const entityCase = getEntityPanelCase(note.domainId);
      if (entityCase) {
        return (
          <>
            <NotesIcon icon={Icons.ANOMALY} />
            {entityCase.description}
          </>
        );
      }
      const task = getTaskById(note.domainId);
      if (task) {
        return (
          <>
            <NotesIcon icon={Icons.TASK} />
            {task.title}
          </>
        );
      }
      return '';
    },
    [caseDetails, getTaskById, getEntityPanelCase],
  );

  if (isLoading || isTasksLoading) {
    return (
      <LoaderContainer>
        <Loader />
      </LoaderContainer>
    );
  }

  const formattedNoteDiscription = (text) => {
    return text.split('\n').map((line, index) => {
      const isUrl = /^https?:\/\/[^\s]+$/.test(line);
      if (isUrl) {
        return (
          <span key={index}>
            <a href={line} target="_blank" rel="noopener noreferrer">
              {line}
            </a>
            <br />
          </span>
        );
      } else {
        return (
          <span key={index}>
            {line}
            <br />
          </span>
        );
      }
    });
  };

  return (
    <StyledNotesAttachmentsTable>
      <div className="header">
        <div className="actions-header">
          <AuthRender
            capability={Capability.NOTES_AND_ATTACHMENTS}
            edit
            delete
            description="Notes and attachments table action menu"
            siteLevel={false}
          >
            {t('notes.actions', 'Actions')}
          </AuthRender>
        </div>
      </div>
      {/* #TODO: This has to be a virtual table */}
      {notesAttachments.map((note) => (
        <div key={note.id} className="note-attachment">
          <div className="row">
            <div className="metadata-container">
              <div className="contributed-by">
                <span>{t('notes.contributed_by', 'Contributed By')}</span>
                <span>{note.updatedBy}</span>
              </div>
              <div className="date-created">
                <span>{t('notes.created_on', 'Created On')}</span>
                <span>{dayjs(note.creationDate).format(DateTimeFormats.ISSUE_DETAIL_DATE)}</span>
              </div>
              <ConditionalRender
                shouldRender={!dayjs(note.updateDate).isSame(dayjs(note.creationDate))}
              >
                <div className="date-updated">
                  <span>{t('notes.last_updated', 'Last Updated')}</span>
                  <span>{dayjs(note.updateDate).format(DateTimeFormats.ISSUE_DETAIL_DATE)}</span>
                </div>
              </ConditionalRender>
            </div>
            <div className="notes-body">
              <span className="notes-title">{getTitle(note)}</span>
              <span className="notes-content">
                {/* #TODO: Will change the icons when we get inspect permission  */}
                {note.category === NotesCategory.SPECIAL_INSTRUCTION && (
                  <IconContainer invalid={isBeforeToday(note.validDateBy)}>
                    <SpecialInstructionIcon icon={Icons.SPECIAL_INSTRUCTION} />
                  </IconContainer>
                )}
                <span style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
                  {formattedNoteDiscription(note.note)}
                </span>
              </span>
              {note.attachments?.length > 0 && (
                <span className="note-scrolling-container-wrapper">
                  <ScrollingContainer>
                    <span className="notes-attachments-container">
                      {note.attachments.map((attachment) => (
                        <div key={attachment.file} className="note-attachment">
                          <div className="attachment-thumbnail">
                            <FileThumbnail
                              src={attachment.file}
                              ext={`.${attachment.contentType.split('/')[1]}`}
                              fileName={attachment.fileName}
                              download
                            />
                          </div>
                          <div className="attachment-name">{attachment.fileName}</div>
                        </div>
                      ))}
                    </span>
                  </ScrollingContainer>
                </span>
              )}
              {note.category === NotesCategory.SPECIAL_INSTRUCTION && (
                <div className="status">
                  <ToggleButton
                    isToggleOn={note.status === SpecialInstructionStatus.ACTIVE}
                    handleOnClick={() => toggleButtonHandler(note)}
                  />
                </div>
              )}
            </div>
            <AuthRender
              capability={Capability.NOTES_AND_ATTACHMENTS}
              edit
              delete
              description="Notes and attachments table action menu"
              siteLevel={false}
            >
              <div className="actions">
                <ActionsMenu
                  containerRef={containerRef}
                  entity={note}
                  menuItems={getAuthorizedMenuItems([note])}
                  menuIconSmall
                  onAction={({ type }) => actionMenuHandler(type, note)}
                  disabledActions={getNotesDisabledActions(note, isNotesAdmin)}
                />
                <AuthRender capability={Capability.INTERNAL_ACCESS} edit siteLevel={false}>
                  <Tooltip
                    title={
                      note.scope === NotesScope.EXTERNAL
                        ? t('notes.external_viewing_on', 'External viewing on')
                        : t('notes.external_viewing_off', 'External viewing off')
                    }
                    placement={placements.TOP}
                    zIndex={elevations.P20}
                  >
                    <span className="view-icon">
                      {note.scope === NotesScope.EXTERNAL ? (
                        <img
                          src={viewing_on}
                          alt="Viewing On"
                          className={
                            getNotesDisabledEyeActions(note, isNotesAdmin)
                              ? 'cursorPointer'
                              : 'cursorNotAllowed'
                          }
                          onClick={() => actionToggleHandler(NotesScope.INTERNAL, note)}
                        />
                      ) : (
                        <img
                          src={viewing_off}
                          alt="Viewing Off"
                          className={
                            getNotesDisabledEyeActions(note, isNotesAdmin)
                              ? 'cursorPointer'
                              : 'cursorNotAllowed'
                          }
                          onClick={() => actionToggleHandler(NotesScope.EXTERNAL, note)}
                        />
                      )}
                    </span>
                  </Tooltip>
                </AuthRender>
              </div>
            </AuthRender>
          </div>
          {note.category === NotesCategory.SPECIAL_INSTRUCTION && (
            <div className="row">
              <div
                className={isBeforeToday(note.validDateBy) ? 'valid-until past-due' : 'valid-until'}
              >
                {`${t('notes.valid_until', 'VALID UNTIL')} : ${dayjs(note.validDateBy).format(
                  DateTimeFormats.ISSUE_DETAIL_DATE,
                )}`}
                {isBeforeToday(note.validDateBy) && ` - ${t('notes.past_due', 'PAST DUE')}`}
              </div>
            </div>
          )}
        </div>
      ))}
    </StyledNotesAttachmentsTable>
  );
};

CaseNotesAttachmentsTable.propTypes = {
  notesAttachments: PropTypes.instanceOf(Array).isRequired,
  editNoteAction: PropTypes.func.isRequired,
  editNoteAndSI: PropTypes.func,
  toggleHandler: PropTypes.func,
  deleteNoteAction: PropTypes.func,
  isLoading: PropTypes.bool,
  entity: PropTypes.instanceOf(Object).isRequired,
};
